/**
 * Trait threshold bonuses
 */
export interface TraitThreshold {
  count: number;
  bonus: TraitBonus;
}

/**
 * Bonus applied at a threshold
 */
export interface TraitBonus {
  attackPercent?: number; // % increase to attack
  defensePercent?: number; // % increase to defense
  healthPercent?: number; // % increase to max health
  speedPercent?: number; // % increase to speed
  attackSpeedPercent?: number; // % increase to attack speed
  abilityPowerPercent?: number; // % increase to ability power
  rangeBonus?: number; // Flat increase to attack range
  critChanceBonus?: number; // % increase to crit chance
  lifestealPercent?: number; // % of damage healed
  damageReductionPercent?: number; // % damage reduction
  description: string; // Human-readable description
}

/**
 * Trait definition
 */
export interface TraitDefinition {
  id: string;
  name: string;
  description: string;
  thresholds: TraitThreshold[];
}

/**
 * All trait definitions
 */
export const TRAIT_DEFINITIONS: Record<string, TraitDefinition> = {
  // TANK - High durability
  tank: {
    id: 'tank',
    name: 'Tank',
    description: 'Frontline warriors that absorb damage',
    thresholds: [
      {
        count: 2,
        bonus: {
          defensePercent: 15,
          description: '+15% Defense',
        },
      },
      {
        count: 4,
        bonus: {
          defensePercent: 35,
          damageReductionPercent: 10,
          description: '+35% Defense, +10% Damage Reduction',
        },
      },
    ],
  },

  // RANGER - Ranged damage dealers
  ranger: {
    id: 'ranger',
    name: 'Ranger',
    description: 'Ranged units that strike from afar',
    thresholds: [
      {
        count: 2,
        bonus: {
          attackPercent: 15,
          description: '+15% Attack',
        },
      },
      {
        count: 4,
        bonus: {
          attackPercent: 30,
          rangeBonus: 1,
          description: '+30% Attack, +1 Range',
        },
      },
    ],
  },

  // SWARM - Strength in numbers
  swarm: {
    id: 'swarm',
    name: 'Swarm',
    description: 'Overwhelm enemies with numbers',
    thresholds: [
      {
        count: 3,
        bonus: {
          attackSpeedPercent: 20,
          description: '+20% Attack Speed',
        },
      },
      {
        count: 5,
        bonus: {
          attackSpeedPercent: 40,
          attackPercent: 15,
          description: '+40% Attack Speed, +15% Attack',
        },
      },
    ],
  },

  // MAGE - Magical damage
  mage: {
    id: 'mage',
    name: 'Mage',
    description: 'Wield powerful magical abilities',
    thresholds: [
      {
        count: 2,
        bonus: {
          abilityPowerPercent: 25,
          description: '+25% Ability Power',
        },
      },
      {
        count: 4,
        bonus: {
          abilityPowerPercent: 50,
          description: '+50% Ability Power',
        },
      },
    ],
  },

  // ASSASSIN - Burst damage
  assassin: {
    id: 'assassin',
    name: 'Assassin',
    description: 'Strike critical targets with deadly precision',
    thresholds: [
      {
        count: 2,
        bonus: {
          critChanceBonus: 15,
          description: '+15% Crit Chance',
        },
      },
      {
        count: 4,
        bonus: {
          critChanceBonus: 30,
          attackPercent: 20,
          description: '+30% Crit Chance, +20% Attack',
        },
      },
    ],
  },

  // BERSERKER - High risk, high reward
  berserker: {
    id: 'berserker',
    name: 'Berserker',
    description: 'Gain power as health decreases',
    thresholds: [
      {
        count: 2,
        bonus: {
          attackPercent: 20,
          lifestealPercent: 10,
          description: '+20% Attack, +10% Lifesteal',
        },
      },
      {
        count: 4,
        bonus: {
          attackPercent: 40,
          lifestealPercent: 20,
          attackSpeedPercent: 15,
          description: '+40% Attack, +20% Lifesteal, +15% Attack Speed',
        },
      },
    ],
  },

  // SUPPORT - Team buffs
  support: {
    id: 'support',
    name: 'Support',
    description: 'Enhance and protect allies',
    thresholds: [
      {
        count: 2,
        bonus: {
          healthPercent: 10,
          description: 'Team +10% Max Health',
        },
      },
      {
        count: 4,
        bonus: {
          healthPercent: 20,
          defensePercent: 10,
          description: 'Team +20% Max Health, +10% Defense',
        },
      },
    ],
  },

  // ORIGIN TRAITS - Secondary trait type

  // HUMAN - Balanced fighters
  human: {
    id: 'human',
    name: 'Human',
    description: 'Adaptable warriors',
    thresholds: [
      {
        count: 2,
        bonus: {
          attackPercent: 8,
          defensePercent: 8,
          description: '+8% Attack, +8% Defense',
        },
      },
      {
        count: 4,
        bonus: {
          attackPercent: 15,
          defensePercent: 15,
          description: '+15% Attack, +15% Defense',
        },
      },
    ],
  },

  // BEAST - Fierce attackers
  beast: {
    id: 'beast',
    name: 'Beast',
    description: 'Savage creatures',
    thresholds: [
      {
        count: 2,
        bonus: {
          attackPercent: 12,
          speedPercent: 10,
          description: '+12% Attack, +10% Speed',
        },
      },
      {
        count: 4,
        bonus: {
          attackPercent: 25,
          speedPercent: 20,
          description: '+25% Attack, +20% Speed',
        },
      },
    ],
  },

  // ELEMENTAL - Magic affinity
  elemental: {
    id: 'elemental',
    name: 'Elemental',
    description: 'Beings of pure magic',
    thresholds: [
      {
        count: 2,
        bonus: {
          abilityPowerPercent: 15,
          description: '+15% Ability Power',
        },
      },
      {
        count: 4,
        bonus: {
          abilityPowerPercent: 30,
          damageReductionPercent: 5,
          description: '+30% Ability Power, +5% Damage Reduction',
        },
      },
    ],
  },

  // MECHANICAL - Consistent damage
  mechanical: {
    id: 'mechanical',
    name: 'Mechanical',
    description: 'Precision-engineered constructs',
    thresholds: [
      {
        count: 2,
        bonus: {
          attackSpeedPercent: 15,
          description: '+15% Attack Speed',
        },
      },
      {
        count: 3,
        bonus: {
          attackSpeedPercent: 25,
          critChanceBonus: 10,
          description: '+25% Attack Speed, +10% Crit Chance',
        },
      },
    ],
  },
};

/**
 * Get trait definition by ID
 */
export function getTraitDefinition(traitId: string): TraitDefinition | undefined {
  return TRAIT_DEFINITIONS[traitId];
}

/**
 * Get all trait IDs
 */
export function getAllTraitIds(): string[] {
  return Object.keys(TRAIT_DEFINITIONS);
}

/**
 * Get traits by category
 */
export function getClassTraits(): string[] {
  return ['tank', 'ranger', 'swarm', 'mage', 'assassin', 'berserker', 'support'];
}

export function getOriginTraits(): string[] {
  return ['human', 'beast', 'elemental', 'mechanical'];
}
